import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ScopeIndicator from '../components/ScopeIndicator';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { holidayAPI } from '../utils/api';
import type { CreateHolidayRequest, Holiday, UpdateHolidayRequest } from '../types/holiday';
import { formatDateInNurseryTimezone, isTodayInNurseryTimezone } from '../utils/timezone';

import { PlusIcon, EditIcon, DeleteIcon } from '../components/icons/SettingsIcons';

const emptyCreateHoliday = (nurseryId: string): CreateHolidayRequest => ({
  name: '',
  date: '',
  is_recurring: false,
  nursery_id: nurseryId,
});

const Holidays: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'holidays' });
  const { t: tc } = useTranslation('common');
  const { currentNursery, nurseriesLoading, userRolePermissionsLoading } = useAuth();
  const { canView, canAdd, canEdit, canDelete } = useRolePermissions();

  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [deletingHoliday, setDeletingHoliday] = useState<Holiday | null>(null);
  const [newHoliday, setNewHoliday] = useState<CreateHolidayRequest>(emptyCreateHoliday(''));
  const [editHoliday, setEditHoliday] = useState<UpdateHolidayRequest>({
    name: '',
    date: '',
    is_recurring: false,
  });

  const nurseryTimezone = currentNursery?.timezone || 'UTC';

  const loadHolidays = useCallback(async () => {
    if (!currentNursery?.nursery_id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await holidayAPI.getHolidays(currentNursery.nursery_id);
      setHolidays(response.holidays || []);
    } catch (err: any) {
      console.error('Failed to load holidays:', err);
      setError(err.response?.data?.message || err.response?.data?.error || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery?.nursery_id, t]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  useEffect(() => {
    setNewHoliday(emptyCreateHoliday(currentNursery?.nursery_id || ''));
  }, [currentNursery?.nursery_id]);

  const filteredHolidays = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return holidays;
    return holidays.filter(holiday =>
      holiday.name.toLowerCase().includes(needle) ||
      holiday.date.includes(needle)
    );
  }, [holidays, searchTerm]);

  const formatDate = (date: string) => {
    try {
      return formatDateInNurseryTimezone(date, nurseryTimezone);
    } catch {
      return date;
    }
  };

  const isToday = (date: string) => {
    try {
      return isTodayInNurseryTimezone(date, nurseryTimezone);
    } catch {
      const today = new Date().toISOString().split('T')[0];
      return date.startsWith(today);
    }
  };

  const handleOpenAddModal = () => {
    setError(null);
    setSuccess(null);
    setNewHoliday(emptyCreateHoliday(currentNursery?.nursery_id || ''));
    setShowAddModal(true);
  };

  const handleCreateHoliday = async () => {
    if (!currentNursery?.nursery_id || !newHoliday.name.trim() || !newHoliday.date) return;

    setSubmitting(true);
    setError(null);
    try {
      await holidayAPI.createHoliday({
        ...newHoliday,
        name: newHoliday.name.trim(),
        nursery_id: currentNursery.nursery_id,
      });
      setSuccess(t('createSuccess'));
      setShowAddModal(false);
      await loadHolidays();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to create holiday:', err);
      setError(err.response?.data?.message || err.response?.data?.error || t('saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setError(null);
    setSuccess(null);
    setEditingHoliday(holiday);
    setEditHoliday({
      name: holiday.name,
      date: holiday.date,
      is_recurring: Boolean(holiday.is_recurring),
    });
  };

  const handleUpdateHoliday = async () => {
    if (!editingHoliday || !editHoliday.name.trim() || !editHoliday.date) return;

    setSubmitting(true);
    setError(null);
    try {
      await holidayAPI.updateHoliday(editingHoliday.id, {
        ...editHoliday,
        name: editHoliday.name.trim(),
      });
      setSuccess(t('updateSuccess'));
      setEditingHoliday(null);
      await loadHolidays();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to update holiday:', err);
      setError(err.response?.data?.message || err.response?.data?.error || t('saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteHoliday = async () => {
    if (!deletingHoliday) return;

    setSubmitting(true);
    setError(null);
    try {
      await holidayAPI.deleteHoliday(deletingHoliday.id);
      setSuccess(t('deleteSuccess'));
      setDeletingHoliday(null);
      await loadHolidays();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to delete holiday:', err);
      setError(err.response?.data?.message || err.response?.data?.error || t('deleteFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (nurseriesLoading || userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
            <p className="text-muted">{tc('loading.loading')}</p>
          </div>
        </Container>
      </Layout>
    );
  }

  if (!canView('holidays')) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="px-3">
          <Alert variant="danger">
            <Alert.Heading>{t('errorTitle')}</Alert.Heading>
            <p className="mb-0">{tc('errors.permissionDenied')}</p>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <div>
            <h2 className="fw-bold text-primary mb-1">{t('title')}</h2>
          </div>
          {currentNursery && canAdd('holidays') && (
            <Button variant="primary" onClick={handleOpenAddModal}>
              <PlusIcon />
              {t('addHoliday')}
            </Button>
          )}
        </div>

        {currentNursery && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentNursery.description} (ID: {currentNursery.nursery_id})
          </Alert>
        )}


        <ScopeIndicator resource="holidays" />

        {!currentNursery && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectNursery', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-4">
            {success}
          </Alert>
        )}

        {currentNursery && (
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white py-3">
              <Row className="align-items-center g-3">
                <Col md={12}>
                  <Form.Control
                    type="search"
                    placeholder={t('searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <h5 className="text-muted">{t('loadingData')}</h5>
                </div>
              ) : filteredHolidays.length === 0 ? (
                <div className="text-center py-5">
                  <div style={{ fontSize: '3rem', opacity: '0.3', marginBottom: '1rem' }}>🎉</div>
                  <h5 className="mb-2 fw-bold text-muted">{t('noTitle')}</h5>
                  <p className="text-muted mb-0 small">
                    {searchTerm ? t('noMatch') : t('noneRegistered')}
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="d-none d-md-block">
                    <div className="table-responsive">
                      <Table hover className="mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>{t('name')}</th>
                            <th>{t('date')}</th>
                            <th>{t('isRecurring')}</th>
                            {(canEdit('holidays') || canDelete('holidays')) && <th className="text-end px-4">{tc('labels.actions')}</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredHolidays.map((holiday) => (
                            <tr key={holiday.id}>
                              <td>
                                <div className="fw-semibold">{holiday.name}</div>
                                {isToday(holiday.date) && (
                                  <Badge bg="success" className="ms-1" style={{ fontSize: '0.7rem' }}>
                                    {tc('status.today')}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <Badge bg="light" text="dark" className="fw-normal">
                                  {formatDate(holiday.date)}
                                </Badge>
                              </td>
                              <td>
                                {holiday.is_recurring ? (
                                  <Badge bg="info" className="fw-normal">{t('recurringYes')}</Badge>
                                ) : (
                                  <Badge bg="secondary" className="fw-normal">{t('recurringNo')}</Badge>
                                )}
                              </td>
                              {(canEdit('holidays') || canDelete('holidays')) && (
                                <td className="text-end px-4">
                                  <div className="d-flex justify-content-end gap-2">
                                    {canEdit('holidays') && (
                                      <span
                                        onClick={() => handleEditHoliday(holiday)}
                                        style={{ cursor: 'pointer' }}
                                        title={tc('buttons.edit')}
                                      >
                                        <EditIcon />
                                      </span>
                                    )}
                                    {canDelete('holidays') && (
                                      <span
                                        onClick={() => setDeletingHoliday(holiday)}
                                        style={{ cursor: 'pointer' }}
                                        title={tc('buttons.delete')}
                                      >
                                        <DeleteIcon />
                                      </span>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  {/* Mobile Card View */}
                  <div className="d-md-none p-3">
                    {filteredHolidays.map((holiday) => (
                      <Card key={holiday.id} className="mb-3 border shadow-sm">
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold">{holiday.name}</h6>
                              {isToday(holiday.date) && (
                                <Badge bg="success" style={{ fontSize: '0.7rem' }}>
                                  {tc('status.today')}
                                </Badge>
                              )}
                            </div>
                            {holiday.is_recurring ? (
                              <Badge bg="info" className="px-2 py-1 small">{t('recurringYes')}</Badge>
                            ) : (
                              <Badge bg="secondary" className="px-2 py-1 small">{t('recurringNo')}</Badge>
                            )}
                          </div>
                          <div className="mb-3">
                            <small className="text-muted d-block mb-1">{t('date')}</small>
                            <Badge bg="light" text="dark" className="px-2 py-1 fw-normal">
                              {formatDate(holiday.date)}
                            </Badge>
                          </div>
                          {(canEdit('holidays') || canDelete('holidays')) && (
                            <div className="d-flex gap-2 pt-2 border-top">
                              {canEdit('holidays') && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditHoliday(holiday)}
                                  className="d-flex align-items-center"
                                >
                                  <EditIcon />
                                  <span className="ms-1">{tc('buttons.edit')}</span>
                                </Button>
                              )}
                              {canDelete('holidays') && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => setDeletingHoliday(holiday)}
                                  className="d-flex align-items-center"
                                >
                                  <DeleteIcon />
                                  <span className="ms-1">{tc('buttons.delete')}</span>
                                </Button>
                              )}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
          <Form onSubmit={(e) => { e.preventDefault(); handleCreateHoliday(); }}>
            <Modal.Header closeButton className="py-2 py-md-3">
              <Modal.Title className="h5 mb-0">{t('addTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-2 py-md-3">
              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium">{t('nameLabel')}</Form.Label>
                <Form.Control
                  size="sm"
                  value={newHoliday.name}
                  maxLength={255}
                  placeholder={t('namePlaceholder')}
                  onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium">{t('dateLabel')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Check
                type="switch"
                id="holiday-recurring"
                label={t('recurringLabel')}
                className="small fw-medium"
                checked={newHoliday.is_recurring}
                onChange={(e) => setNewHoliday(prev => ({ ...prev, is_recurring: e.target.checked }))}
              />
              <Form.Text className="text-muted small">{t('recurringHint')}</Form.Text>
            </Modal.Body>
            <Modal.Footer className="py-2 py-md-3">
              <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={submitting}>{tc('buttons.cancel')}</Button>
              <Button type="submit" variant="primary" disabled={submitting || !newHoliday.name.trim() || !newHoliday.date}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('loading.saving')}
                  </>
                ) : (
                  <>
                    <PlusIcon />
                    {t('addHoliday')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={!!editingHoliday} onHide={() => setEditingHoliday(null)} centered>
          <Form onSubmit={(e) => { e.preventDefault(); handleUpdateHoliday(); }}>
            <Modal.Header closeButton className="py-2 py-md-3">
              <Modal.Title className="h5 mb-0">{t('editTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-2 py-md-3">
              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium">{t('nameLabel')}</Form.Label>
                <Form.Control
                  size="sm"
                  value={editHoliday.name}
                  maxLength={255}
                  onChange={(e) => setEditHoliday(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium">{t('dateLabel')}</Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={editHoliday.date}
                  onChange={(e) => setEditHoliday(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Check
                type="switch"
                id="edit-holiday-recurring"
                label={t('recurringLabel')}
                className="small fw-medium"
                checked={editHoliday.is_recurring}
                onChange={(e) => setEditHoliday(prev => ({ ...prev, is_recurring: e.target.checked }))}
              />
              <Form.Text className="text-muted small">{t('recurringHint')}</Form.Text>
            </Modal.Body>
            <Modal.Footer className="py-2 py-md-3">
              <Button variant="secondary" onClick={() => setEditingHoliday(null)} disabled={submitting}>{tc('buttons.cancel')}</Button>
              <Button type="submit" variant="primary" disabled={submitting || !editHoliday.name.trim() || !editHoliday.date}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('loading.saving')}
                  </>
                ) : (
                  <>
                    <EditIcon />
                    {tc('buttons.saveChanges')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={!!deletingHoliday} onHide={() => setDeletingHoliday(null)} centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('deleteTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-2 py-md-3">
            {deletingHoliday && (
              <Alert variant="warning" className="mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {t('deleteConfirm', { name: deletingHoliday.name })}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer className="py-2 py-md-3">
            <Button variant="secondary" onClick={() => setDeletingHoliday(null)} disabled={submitting}>{tc('buttons.cancel')}</Button>
            <Button variant="danger" onClick={handleDeleteHoliday} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {tc('loading.deleting')}
                </>
              ) : (
                <>
                  <DeleteIcon />
                  {tc('buttons.delete')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Holidays;
